// ============================================================================
//  GlossaryCmsTab.jsx — el CMS del glosario
//
//  ── QUE HABIA AQUI ─────────────────────────────────────────────────────────
//      const GlossaryCmsTab = makeMockTab('Glossary CMS', '📖');
//
//  Un cartel. La pestaña salia en el menu con su icono, se podia pulsar, y no
//  hacia nada. Era la unica de las diez del Command Center que era de mentira,
//  mientras el glosario —13.546 palabras, el contenido mas grande de NSL— solo
//  se podia tocar entrando a la base a mano.
//
//  ── DE DONDE SALEN LOS NUMEROS ─────────────────────────────────────────────
//  De `/api/glossary/sections`, que los cuenta en la base. Ninguno esta escrito
//  aqui: en Mini Games, la lista con los conteos a mano llevaba ocho de diez
//  desfasados —uno decia 50 teniendo 2.887— y era justo la pantalla donde se
//  decidia cuanto contenido generar.
//
//  Los topes de cada casilla vienen de `/api/glossary/admin/limites`, que los
//  deriva de las columnas. Repartidos, el dia que se amplie una columna la
//  pantalla se queda corta — o al reves, deja escribir lo que el servidor va a
//  rechazar, que es el fallo que ya costo caro en los chats.
//
//  ── LA PAGINACION ES DE VERDAD ─────────────────────────────────────────────
//  Se piden 50 palabras por pagina con `offset`, no las 13.546 de golpe. El
//  panel del alumno si se las trae enteras —4,2 MB, y eso es la Fase 4— pero
//  una pantalla de administracion no tiene por que repetir ese error.
// ============================================================================

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BookMarked, Search, Plus, Pencil, Trash2, X, Save,
  ChevronLeft, ChevronRight, AlertTriangle, Loader,
} from 'lucide-react';
import './GlossaryCmsTab.css';

function useT(dark) {
  const bg = dark ? '#141414' : '#e0e5ec';
  const sd = dark ? '#090909' : '#a3b1c6';
  const sl = dark ? '#222222' : '#ffffff';
  const tx = dark ? '#e8e8e8' : '#1a1a1a';
  const tx2 = dark ? '#888888' : '#7a8499';
  const ac = dark ? '#00FFFF' : '#4f46e5';
  const ok = dark ? '#00ff88' : '#00cc66';
  const er = dark ? '#ff6b6b' : '#f87171';
  const wrn = dark ? '#f59e0b' : '#d97706';
  return {
    dark, bg, sd, sl, tx, tx2, ac, ok, er, wrn,
    nmd: `-6px 6px 14px ${sd},6px -6px 14px ${sl}`,
    nxs: `-2px 2px 5px ${sd},2px -2px 5px ${sl}`,
    ism: `inset -3px 3px 8px ${sd},inset 3px -3px 8px ${sl}`,
    ixs: `inset -2px 2px 5px ${sd},inset 2px -2px 5px ${sl}`,
  };
}

const POR_PAGINA = 50;

/** El orden en que se enseñan los campos: primero lo que define la palabra. */
const CAMPOS = [
  'en', 'es', 'ph', 'note', 'past', 'pp',
  'level', 'section', 'subsection', 'subgroup', 'sort_order',
];

const ETIQUETAS = {
  en: 'Inglés', es: 'Español', ph: 'Fonética (IPA)', note: 'Nota',
  past: 'Pasado', pp: 'Participio', level: 'Nivel', section: 'Sección',
  subsection: 'Subsección', subgroup: 'Subgrupo', sort_order: 'Orden',
};

function cab() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
   UNA CASILLA, CON SU TOPE Y SU CONTADOR

   El contador se enseña SIEMPRE, no al acercarse al limite: con la casilla
   vacia, un tope que solo aparece al chocarse no esta puesto para quien
   escribe. El color si puede ser discreto de lejos y alarmante de cerca; la
   cifra, no.
══════════════════════════════════════════════════════════════════════════ */
function Casilla({ campo, valor, tope, obligatorio, onChange, t, largo }) {
  const texto = valor === null || valor === undefined ? '' : String(valor);
  const cerca = tope && texto.length > tope * 0.9;
  const Etiqueta = largo ? 'textarea' : 'input';
  return (
    <div className="gcms-campo">
      <label className="gcms-campo__label" style={{ color: t.tx2 }}>
        <span>{ETIQUETAS[campo] || campo}{obligatorio ? ' *' : ''}</span>
        {tope > 0 && (
          <span className="gcms-campo__cuenta"
                style={{ color: cerca ? t.er : t.tx2 }}>
            {texto.length}/{tope}
          </span>
        )}
      </label>
      <Etiqueta
        className="gcms-campo__input"
        rows={largo ? 3 : undefined}
        maxLength={tope || undefined}
        value={texto}
        onChange={(e) => onChange(campo, e.target.value)}
        style={{ background: t.bg, color: t.tx, border: `1px solid ${t.tx2}33` }}
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   EL FORMULARIO — sirve para crear y para corregir
══════════════════════════════════════════════════════════════════════════ */
function Formulario({ palabra, limites, obligatorios, onGuardar, onCerrar,
                      t, guardando, error, titulo }) {
  const [borrador, setBorrador] = useState(() => ({ ...palabra }));
  const set = useCallback((campo, valor) => {
    setBorrador((b) => ({ ...b, [campo]: valor }));
  }, []);

  const faltan = obligatorios.filter(
    (c) => !String(borrador[c] ?? '').trim());

  return (
    <div className="gcms-form" style={{ background: t.bg, boxShadow: t.nmd }}>
      <div className="gcms-form__hd">
        <span style={{ color: t.tx, fontWeight: 800, fontSize: 12 }}>{titulo}</span>
        <button onClick={onCerrar} className="gcms-btn-icono"
                style={{ color: t.tx2 }}><X size={13} /></button>
      </div>

      <div className="gcms-form__grid">
        {CAMPOS.map((c) => (
          <Casilla
            key={c}
            campo={c}
            valor={borrador[c]}
            tope={limites[c] || 0}
            obligatorio={obligatorios.includes(c)}
            onChange={set}
            t={t}
            largo={c === 'note'}
          />
        ))}
      </div>

      {faltan.length > 0 && (
        <div className="gcms-aviso" style={{ background: `${t.wrn}12`,
             border: `1px solid ${t.wrn}33`, color: t.wrn }}>
          Falta rellenar: {faltan.map((c) => ETIQUETAS[c] || c).join(', ')}
        </div>
      )}

      {/* El mensaje del servidor, tal cual: un 409 dice con cuál choca y un
          422 qué campo se pasa. «Hubo un problema» tira esa información. */}
      {error && (
        <div className="gcms-aviso" style={{ background: `${t.er}12`,
             border: `1px solid ${t.er}33`, color: t.er }}>
          <AlertTriangle size={11} style={{ flexShrink: 0 }} /> {error}
        </div>
      )}

      <div className="gcms-form__pie">
        <button onClick={onCerrar} className="gcms-btn"
                style={{ border: `1px solid ${t.tx2}33`, color: t.tx2,
                         background: 'transparent' }}>Cancelar</button>
        <button
          onClick={() => onGuardar(borrador)}
          disabled={guardando || faltan.length > 0}
          className="gcms-btn gcms-btn--ok"
          style={{ border: `1px solid ${t.ok}55`, color: t.ok,
                   background: `${t.ok}12`,
                   opacity: guardando || faltan.length ? 0.5 : 1 }}>
          <Save size={11} /> {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   LA PESTAÑA
══════════════════════════════════════════════════════════════════════════ */
export default function GlossaryCmsTab({ dark = true, HICOLOR }) {
  const t = useT(dark);
  const hi = HICOLOR || (dark ? t.ac : '#4f46e5');

  const [indice, setIndice] = useState([]);      // de /sections
  const [limites, setLimites] = useState({});
  const [obligatorios, setObligatorios] = useState(['section', 'subsection', 'en', 'es']);

  const [seccion, setSeccion] = useState(null);
  const [subseccion, setSubseccion] = useState(null);
  const [subgrupo, setSubgrupo] = useState(null);
  const [busqueda, setBusqueda] = useState('');

  const [palabras, setPalabras] = useState([]);
  const [pagina, setPagina] = useState(0);
  const [cargando, setCargando] = useState(false);

  const [editando, setEditando] = useState(null);   // objeto o 'nueva'
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState('');
  const [aviso, setAviso] = useState('');
  const [borrandoId, setBorrandoId] = useState(null);

  // ── El índice y los topes, una vez ────────────────────────────────────
  useEffect(() => {
    fetch('/api/glossary/sections', { headers: cab() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => Array.isArray(d) && setIndice(d))
      .catch(() => {});
    fetch('/api/glossary/admin/limites', { headers: cab() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setLimites(d.limites || {});
        if (Array.isArray(d.obligatorios)) setObligatorios(d.obligatorios);
      })
      .catch(() => {});
  }, []);

  // ── El árbol, construido de lo que dice la base ───────────────────────
  const arbol = useMemo(() => {
    const secciones = new Map();
    let total = 0;
    for (const fila of indice) {
      total += fila.count;
      if (!secciones.has(fila.section)) {
        secciones.set(fila.section, { nombre: fila.section, total: 0, subs: new Map() });
      }
      const sec = secciones.get(fila.section);
      sec.total += fila.count;
      if (!sec.subs.has(fila.subsection)) {
        sec.subs.set(fila.subsection, { nombre: fila.subsection, total: 0, subgrupos: [] });
      }
      const sub = sec.subs.get(fila.subsection);
      sub.total += fila.count;
      if (fila.subgroup) sub.subgrupos.push({ nombre: fila.subgroup, total: fila.count });
    }
    return { secciones: [...secciones.values()], total };
  }, [indice]);

  const subsecciones = useMemo(() => {
    const sec = arbol.secciones.find((s) => s.nombre === seccion);
    return sec ? [...sec.subs.values()] : [];
  }, [arbol, seccion]);

  const subgrupos = useMemo(() => {
    const sub = subsecciones.find((s) => s.nombre === subseccion);
    return sub ? sub.subgrupos : [];
  }, [subsecciones, subseccion]);

  // ── Las palabras de la página actual ──────────────────────────────────
  const traePalabras = useCallback(async () => {
    if (!seccion && !busqueda.trim()) { setPalabras([]); return; }
    setCargando(true);
    const p = new URLSearchParams();
    if (seccion) p.set('section', seccion);
    if (subseccion) p.set('subsection', subseccion);
    if (subgrupo) p.set('subgroup', subgrupo);
    if (busqueda.trim()) p.set('q', busqueda.trim());
    p.set('limit', String(POR_PAGINA));
    p.set('offset', String(pagina * POR_PAGINA));
    try {
      const r = await fetch(`/api/glossary/words?${p}`, { headers: cab() });
      setPalabras(r.ok ? await r.json() : []);
    } catch { setPalabras([]); } finally { setCargando(false); }
  }, [seccion, subseccion, subgrupo, busqueda, pagina]);

  useEffect(() => { traePalabras(); }, [traePalabras]);

  function elegirSeccion(nombre) {
    setSeccion(nombre === seccion ? null : nombre);
    setSubseccion(null); setSubgrupo(null); setPagina(0);
  }
  function elegirSubseccion(nombre) {
    setSubseccion(nombre === subseccion ? null : nombre);
    setSubgrupo(null); setPagina(0);
  }
  function elegirSubgrupo(nombre) {
    setSubgrupo(nombre === subgrupo ? null : nombre); setPagina(0);
  }

  // ── Guardar (crear o corregir) ────────────────────────────────────────
  async function guarda(borrador) {
    setGuardando(true); setErrorForm(''); setAviso('');
    try {
      const esNueva = editando === 'nueva';
      let r;
      if (esNueva) {
        r = await fetch('/api/glossary/admin/words', {
          method: 'POST', headers: cab(), body: JSON.stringify(borrador),
        });
      } else {
        // Sólo lo que cambió: mandar la palabra entera para arreglar una tilde
        // invita a pisar un campo que no se estaba mirando.
        const cambios = {};
        for (const [k, v] of Object.entries(borrador)) {
          if (k === 'id' || k === 'created_at') continue;
          if (String(v ?? '') !== String(editando[k] ?? '')) cambios[k] = v;
        }
        if (Object.keys(cambios).length === 0) {
          setErrorForm('No has cambiado nada.'); return;
        }
        r = await fetch(`/api/glossary/admin/words/${editando.id}`, {
          method: 'PUT', headers: cab(),
          body: JSON.stringify({ campos: cambios }),
        });
      }
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErrorForm(d?.detail || `No se pudo guardar (HTTP ${r.status}).`); return; }
      setAviso(esNueva ? `Añadida «${borrador.en}»` : `Corregida «${borrador.en}»`);
      setEditando(null);
      traePalabras();
    } catch {
      setErrorForm('No se pudo hablar con el servidor. Nada ha cambiado.');
    } finally { setGuardando(false); }
  }

  async function borra(palabra) {
    setBorrandoId(null); setAviso('');
    try {
      const r = await fetch(`/api/glossary/admin/words/${palabra.id}`, {
        method: 'DELETE', headers: cab(),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setAviso(d?.detail || 'No se pudo borrar.'); return; }
      setAviso(`Borrada «${palabra.en}» — queda apuntada en la auditoría`);
      traePalabras();
    } catch { setAviso('No se pudo hablar con el servidor.'); }
  }

  const nueva = {
    section: seccion || '', subsection: subseccion || '',
    subgroup: subgrupo || '', en: '', es: '', ph: '', note: '',
    past: '', pp: '', level: '', sort_order: 0, is_irregular: false,
  };

  return (
    <div className="gcms-root" style={{ background: t.bg, color: t.tx }}>

      {/* ── Cabecera ── */}
      <div className="gcms-top" style={{ background: t.bg, boxShadow: t.nmd }}>
        <div className="gcms-top__left">
          <div className="gcms-top__icono"
               style={{ background: `${hi}14`, color: hi,
                        boxShadow: `${t.nxs},0 0 0 1.5px ${hi}44` }}>
            <BookMarked size={15} />
          </div>
          <div>
            <div className="gcms-top__titulo" style={{ color: t.tx }}>Glossary CMS</div>
            <div className="gcms-top__sub" style={{ color: t.tx2 }}>
              Neon DB ·{' '}
              <span style={{ fontFamily: 'JetBrains Mono,monospace', color: hi }}>
                glossary_words
              </span>{' '}
              ·{' '}
              <span style={{ color: t.ok, fontWeight: 700 }}>
                {arbol.total.toLocaleString()} palabras · {arbol.secciones.length} secciones
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={() => { setEditando('nueva'); setErrorForm(''); }}
          className="gcms-btn gcms-btn--hi"
          style={{ border: `1px solid ${hi}55`, color: hi, background: `${hi}12` }}>
          <Plus size={12} /> Nueva palabra
        </button>
      </div>

      {aviso && (
        <div className="gcms-aviso gcms-aviso--suelto"
             style={{ background: `${t.ok}10`, border: `1px solid ${t.ok}33`, color: t.ok }}>
          {aviso}
        </div>
      )}

      {editando && (
        <Formulario
          palabra={editando === 'nueva' ? nueva : editando}
          limites={limites}
          obligatorios={obligatorios}
          onGuardar={guarda}
          onCerrar={() => { setEditando(null); setErrorForm(''); }}
          t={t}
          guardando={guardando}
          error={errorForm}
          titulo={editando === 'nueva' ? 'Nueva palabra' : `Corregir «${editando.en}»`}
        />
      )}

      {/* ── El árbol ── */}
      <div className="gcms-arbol" style={{ background: t.bg, boxShadow: t.nmd }}>
        <div className="gcms-arbol__fila">
          {arbol.secciones.map((s) => (
            <button key={s.nombre} onClick={() => elegirSeccion(s.nombre)}
              className="gcms-pill"
              style={{ background: seccion === s.nombre ? `${hi}18` : t.bg,
                       boxShadow: seccion === s.nombre ? `${t.ism},0 0 0 1px ${hi}44` : t.ixs,
                       color: seccion === s.nombre ? hi : t.tx2 }}>
              {s.nombre} <span className="gcms-pill__n">{s.total}</span>
            </button>
          ))}
        </div>

        {seccion && subsecciones.length > 0 && (
          <div className="gcms-arbol__fila gcms-arbol__fila--sub">
            {subsecciones.map((s) => (
              <button key={s.nombre} onClick={() => elegirSubseccion(s.nombre)}
                className="gcms-pill"
                style={{ background: subseccion === s.nombre ? `${t.ok}18` : t.bg,
                         boxShadow: subseccion === s.nombre ? `${t.ism},0 0 0 1px ${t.ok}44` : t.ixs,
                         color: subseccion === s.nombre ? t.ok : t.tx2 }}>
                {s.nombre} <span className="gcms-pill__n">{s.total}</span>
              </button>
            ))}
          </div>
        )}

        {subseccion && subgrupos.length > 0 && (
          <div className="gcms-arbol__fila gcms-arbol__fila--sub">
            {subgrupos.map((s) => (
              <button key={s.nombre} onClick={() => elegirSubgrupo(s.nombre)}
                className="gcms-pill"
                style={{ background: subgrupo === s.nombre ? `${t.wrn}18` : t.bg,
                         boxShadow: subgrupo === s.nombre ? `${t.ism},0 0 0 1px ${t.wrn}44` : t.ixs,
                         color: subgrupo === s.nombre ? t.wrn : t.tx2 }}>
                {s.nombre} <span className="gcms-pill__n">{s.total}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Buscador ── */}
      <div className="gcms-buscador" style={{ background: t.bg, boxShadow: t.ixs }}>
        <Search size={12} style={{ color: t.tx2, flexShrink: 0 }} />
        <input
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setPagina(0); }}
          placeholder="Buscar en inglés o español…"
          style={{ color: t.tx }}
        />
        {cargando && <Loader size={12} className="gcms-girando" style={{ color: t.tx2 }} />}
      </div>

      {/* ── La tabla ── */}
      <div className="gcms-tabla" style={{ background: t.bg, boxShadow: t.nmd }}>
        <div className="gcms-tabla__cab"
             style={{ color: t.tx2, borderBottom: `1px solid ${t.tx2}22` }}>
          <span>ID</span><span>Inglés</span><span>Español</span>
          <span>Fonética</span><span>Dónde</span><span />
        </div>

        {palabras.length === 0 && !cargando && (
          <div className="gcms-vacio" style={{ color: t.tx2 }}>
            {seccion || busqueda.trim()
              ? 'Nada por aquí con esos filtros.'
              : 'Elige una sección arriba, o busca una palabra.'}
          </div>
        )}

        {palabras.map((p, i) => (
          <div key={p.id} className="gcms-fila"
               style={{ borderBottom: `1px solid ${t.tx2}14`,
                        background: i % 2 ? 'transparent'
                          : (dark ? 'rgba(255,255,255,.012)' : 'rgba(0,0,0,.012)') }}>
            <span className="gcms-mono" style={{ color: t.tx2 }}>#{p.id}</span>
            <span style={{ color: hi, fontWeight: 600 }}>{p.en}</span>
            <span style={{ color: t.tx }}>{p.es}</span>
            <span className="gcms-mono" style={{ color: t.tx2 }}>{p.ph || '—'}</span>
            <span style={{ color: t.tx2, fontSize: 9 }}>
              {p.subsection}{p.subgroup ? ` / ${p.subgroup}` : ''}
            </span>
            <span className="gcms-fila__acciones">
              {borrandoId === p.id ? (
                <>
                  <button onClick={() => borra(p)} className="gcms-btn-icono"
                          style={{ color: t.er, fontSize: 9, fontWeight: 700 }}>
                    Sí, borrar
                  </button>
                  <button onClick={() => setBorrandoId(null)} className="gcms-btn-icono"
                          style={{ color: t.tx2, fontSize: 9 }}>No</button>
                </>
              ) : (
                <>
                  <button onClick={() => { setEditando(p); setErrorForm(''); }}
                          title="Corregir" className="gcms-btn-icono"
                          style={{ color: t.tx2 }}><Pencil size={11} /></button>
                  <button onClick={() => setBorrandoId(p.id)}
                          title="Borrar" className="gcms-btn-icono"
                          style={{ color: t.tx2 }}><Trash2 size={11} /></button>
                </>
              )}
            </span>
          </div>
        ))}
      </div>

      {/* ── Paginación de verdad: 50 por viaje, no las 13.546 ── */}
      {(palabras.length > 0 || pagina > 0) && (
        <div className="gcms-pager">
          <span style={{ fontSize: 9, color: t.tx2 }}>
            página <strong style={{ color: t.tx }}>{pagina + 1}</strong> ·{' '}
            {palabras.length} en pantalla
          </span>
          <div className="gcms-pager__btns">
            <button disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}
              style={{ background: t.bg, boxShadow: pagina ? t.nxs : t.ixs,
                       color: pagina ? t.tx : t.tx2, opacity: pagina ? 1 : 0.4 }}>
              <ChevronLeft size={11} />
            </button>
            <button disabled={palabras.length < POR_PAGINA}
              onClick={() => setPagina((p) => p + 1)}
              style={{ background: t.bg,
                       boxShadow: palabras.length < POR_PAGINA ? t.ixs : t.nxs,
                       color: palabras.length < POR_PAGINA ? t.tx2 : t.tx,
                       opacity: palabras.length < POR_PAGINA ? 0.4 : 1 }}>
              <ChevronRight size={11} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
